<template>
  <button
    v-if="isReservationStatus && booking.leftToPay && booking.leftToPay > 0"
    class="button-primary primary-normal small action-button"
    :disabled="isPaymentDisabled"
    @click="handlePayment"
  >
    {{ t('reservations.payBalance') }}
  </button>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { BookingSummary } from '@/services/BookingService.interface'
  import { UserInfo } from '@/services/AuthService.interface'
  import { computed } from 'vue'

  // Composables
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import Cookies from 'js-cookie'
  import { CookieName } from '@/config/cookies'
  import { LocaleEnum } from '@/config'

  // Props
  const props = defineProps<{
    userId: UserInfo['iresa_id'] | null
    booking: BookingSummary
  }>()

  // i18n instance
  const { t } = useI18n()

  // Computed property to check if payment should be disabled
  const isPaymentDisabled = computed(() => {
    return props.booking.reference.startsWith('BO') && 
           (props.booking.packageCode === 'PFF' || props.booking.packageCode === 'PFPF')
  })

  // Function to handle legacy locale conversion
  const handleLegacyLocale = (url: URL): void => {
    const currentLocale = url.searchParams.get('locale')
    if (currentLocale === LocaleEnum.EN_GB) {
      url.searchParams.set('locale', LocaleEnum.EN_US)
    }
  }

  // Handle payment logic
  const handlePayment = (): void => {
    if (!props.userId || !props.booking) {
      console.error(
        'User ID and booking information are required to proceed with payment.'
      )
      return
    }

    let paymentLink: string | undefined = props.booking.paymentLink
    if (!paymentLink) {
      console.error('Missing payment link')
      return
    }

    const company = Cookies.get(CookieName.COMPANY)
    const updatedPaymentLink = new URL(paymentLink, window.location.origin)

    // Apply legacy locale conversion
    handleLegacyLocale(updatedPaymentLink)

    // Add company parameter if available
    if (company) {
      updatedPaymentLink.searchParams.set('company', company)
    }

    // Open the updated payment link in a new tab
    window.open(updatedPaymentLink.href, '_blank')
  }

  const { isReservationStatus } = useBookingStatus(props.booking)
</script>

<style scoped>
  @media (max-width: 768px) {
    .action-button {
      width: 100%; /* Set buttons to full width */
      max-width: 100%; /* Ensure no max width restriction */
    }
  }
</style>
